import React, { JSX, useState, useRef } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { supabase } from "../../lib/supabase";
import "./EditProfileModal.css";

type EditProfileModalProps = {
  open: boolean;
  onClose: () => void;
  email: string;
  displayName: string;
  avatarUrl?: string;
};

export default function EditProfileModal({
  open,
  onClose,
  email,
  displayName: initialDisplayName,
  avatarUrl: initialAvatarUrl = "",
}: EditProfileModalProps): JSX.Element | null {
  const { updateProfile } = useAuth();
  const [displayName, setDisplayName] = useState(initialDisplayName);
  const [avatarUrl, setAvatarUrl] = useState(initialAvatarUrl);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Sync state when prop changes or modal opens
  React.useEffect(() => {
    setDisplayName(initialDisplayName);
    setAvatarUrl(initialAvatarUrl);
  }, [initialDisplayName, initialAvatarUrl, open]);

  if (!open) return null;

  const handleSaveChanges = async () => {
    if (displayName !== initialDisplayName || avatarUrl !== initialAvatarUrl) {
      setLoading(true);
      await updateProfile({ displayName, avatarUrl });
      setLoading(false);
    }
    onClose();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setUploading(true);

      if (!event.target.files || event.target.files.length === 0) {
        throw new Error('You must select an image to upload.');
      }

      const file = event.target.files[0];
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random()}.${fileExt}`;
      const filePath = `${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file);

      if (uploadError) {
        throw uploadError;
      }

      const { data } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      setAvatarUrl(data.publicUrl);
    } catch (error) {
      alert('Error uploading avatar!');
      console.error(error);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="edit-profile-modal">
      <div className="overlay" onClick={onClose}>
        <div className="modal-container" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="modal-header">
            <h2 className="modal-title">Edit Profile</h2>
            <button className="modal-close-button" onClick={onClose}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          {/* Profile Picture */}
          <div className="form-group">
            <label className="label">Profile Picture</label>
            <div className="profile-picture-row">
              <div className="avatar-small">
                {avatarUrl ? (
                  <img src={avatarUrl} alt="Profile" />
                ) : (
                  <span className="avatar-initial">
                    {displayName.charAt(0).toUpperCase()}
                  </span>
                )}
              </div>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept="image/*"
                style={{ display: 'none' }}
              />
              <button
                className="change-button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                {uploading ? 'Uploading...' : 'Change'}
              </button>
            </div>
          </div>

          {/* Email Field */}
          <div className="form-group">
            <label className="label">Email</label>
            <input
              className="input"
              type="email"
              value={email}
              disabled
              readOnly
              style={{ backgroundColor: '#f3f4f6', color: '#6b7280', cursor: 'default' }}
            />
          </div>

          {/* Display Name Field */}
          <div className="form-group">
            <label className="label">Display Name</label>
            <input
              className="input"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
          </div>

          {/* Save Button */}
          <button
            className="save-button"
            onClick={handleSaveChanges}
            disabled={loading}
          >
            {loading ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}
